import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Stack,
  IconButton,
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon, 
  Close as CloseIcon,
  CheckCircle,
  Info,
  Warning,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useAppSelector } from '../../../../../app/hooks';
import { selectCalculationAuditTrail } from '../../../../../services/ContractService/contractEstimateSlice';
import { CalculationStep } from '../../../../../services/CalculatorService/types/calculatorTypes';

interface AuditTrailDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Get icon for step type
 */
const getStepIcon = (stepType: string | undefined) => {
  switch (stepType?.toLowerCase()) {
    case 'success':
    case 'complete':
      return <CheckCircle color="success" />;
    case 'warning':
      return <Warning color="warning" />;
    case 'error':
      return <ErrorIcon color="error" />;
    default:
      return <Info color="info" />;
  }
};

/**
 * Get color for step severity
 */
const getStepColor = (stepType: string | undefined): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (stepType?.toLowerCase()) {
    case 'success':
    case 'complete':
      return 'success';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    default:
      return 'info';
  }
};

export const AuditTrailDialog: React.FC<AuditTrailDialogProps> = ({
  open,
  onClose,
}) => {
  const auditTrail = useAppSelector(selectCalculationAuditTrail);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Calculation Audit Trail</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        {auditTrail.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No audit trail available
          </Typography>
        ) : (
          <Stack spacing={1}>
            {auditTrail.map((step: CalculationStep, index: number) => (
              <Accordion key={index} defaultExpanded={index === 0}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: '#f8fafc',
                    '&:hover': { backgroundColor: '#f1f5f9' }
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                    {getStepIcon(step.stepType)}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {step.stepName || `Step ${index + 1}`}
                      </Typography>
                      {step.description && (
                        <Typography variant="body2" color="text.secondary">
                          {step.description}
                        </Typography>
                      )}
                    </Box>
                    {step.stepType && (
                      <Chip 
                        label={step.stepType} 
                        size="small" 
                        color={getStepColor(step.stepType)}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: '#ffffff' }}>
                  <Stack spacing={2}>
                    {/* Values Section */}
                    {(step.inputValue != null || step.outputValue != null) && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                          Values
                        </Typography>
                        <Stack direction="row" spacing={3}>
                          {step.inputValue != null && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">Input</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                ${step.inputValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Box>
                          )}
                          {step.outputValue != null && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">Output</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500, color: 'success.main' }}>
                                ${step.outputValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </Box>
                    )}

                    {/* Formula Section */}
                    {step.formula && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                          Formula
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: '#f1f5f9',
                            p: 1.5,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                          }}
                        >
                          {step.formula}
                        </Box>
                      </Box>
                    )}

                    {/* Metadata Section */}
                    {step.metadata && Object.keys(step.metadata).length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                          Metadata
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            backgroundColor: '#1e293b',
                            color: '#e2e8f0',
                            p: 2,
                            borderRadius: 1,
                            overflow: 'auto',
                            fontSize: '0.875rem',
                            fontFamily: 'monospace',
                            maxHeight: '300px',
                            m: 0,
                          }}
                        >
                          {JSON.stringify(step.metadata, null, 2)}
                        </Box>
                      </Box>
                    )}

                    {/* Details Section (legacy/fallback) */}
                    {step.details && Object.keys(step.details).length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                          Details
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            backgroundColor: '#1e293b',
                            color: '#e2e8f0',
                            p: 2,
                            borderRadius: 1,
                            overflow: 'auto',
                            fontSize: '0.875rem',
                            fontFamily: 'monospace',
                            maxHeight: '300px',
                            m: 0,
                          }}
                        >
                          {JSON.stringify(step.details, null, 2)}
                        </Box>
                      </Box>
                    )}

                    {/* No data message */}
                    {!step.formula && 
                     !step.metadata && 
                     !step.details && 
                     step.inputValue == null && 
                     step.outputValue == null && (
                      <Typography color="text.secondary" variant="body2">
                        No additional details available
                      </Typography>
                    )}

                    {/* Timestamp */}
                    {step.timestamp && (
                      <Typography variant="caption" color="text.secondary">
                        Timestamp: {new Date(step.timestamp).toLocaleString()}
                      </Typography>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
